// Account step (stories 13-20): one list, Demo mode first, accounts with a saved sizing next (A-Z),
// then the rest (A-Z). Reuses the existing owned-accounts client and the open-opportunity rule
// (0 -> account only, 1 -> automatic, 2+ -> inline picker). Demo never writes to Salesforce.
//
// Picking an account with a saved sizing restores it (story 19): the latest snapshot's story JSON is
// read back through the v2 -> v3 migration and hydrates every slice, including the Tool consolidation
// checklist, with any re-check flags. Demo mode and accounts without a saved sizing restore nothing.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { useApi } from '../../../_shared/hooks';
import {
  fetchOwnedAccounts,
  type OwnedAccount,
  type OwnedAccountOpenOpportunity,
  type OwnedAccountsResponse,
} from '../../api-client';
import { buildAccountList, hasSavedSizing, opportunityModeFor } from '../../flow/account-list';
import { calculatorActions, useCalculatorStore } from '../../state/calculator-store';
import { applyLoaded } from '../../state/load-paths';
import { fetchSavedSizing } from '../../state/restore';
import { formatAnnualValue, formatSavedDate } from '../account-link-model';
import { showNotice } from '../../state/notice-store';
import { ACCOUNT_STEP } from '../../flow/step-model';
import { Ph } from '../Icon';

type AccountsLoad =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: OwnedAccountsResponse };

/**
 * One fetch per visit of the step, same shape as v2's `AccountLinkMenu`. `attempt` re-runs the
 * effect for Retry.
 */
function useOwnedAccounts(): { load: AccountsLoad; retry: () => void } {
  const { apiFetch } = useApi();
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState<AccountsLoad>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchOwnedAccounts(apiFetch)
      .then((data) => {
        if (!cancelled) setLoad({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoad({ status: 'error', message: err instanceof Error ? err.message : 'We couldn’t load your accounts.' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, attempt]);

  const retry = useCallback(() => {
    setLoad({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);
  return { load, retry };
}

type RestoreNotice =
  | { kind: 'restoring'; name: string }
  | { kind: 'unreadable'; name: string }
  | { kind: 'error'; name: string; message: string };

export function AccountStep() {
  const account = useCalculatorStore((s) => s.account);
  const { apiFetch } = useApi();
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [notice, setNotice] = useState<RestoreNotice | null>(null);
  // Picking another account while a restore is in flight must not let the first one land.
  const restoreToken = useRef(0);
  const { load, retry } = useOwnedAccounts();
  const accountList = load.status === 'ready' ? load.data.accounts : [];

  const entries = buildAccountList(accountList, query);
  const demoSelected = account?.kind === 'demo';
  const selectedAccountId = account?.kind === 'account' ? account.accountId : null;

  function link(a: OwnedAccount, opp?: OwnedAccountOpenOpportunity) {
    const token = ++restoreToken.current;
    calculatorActions.linkAccount({ id: a.id, name: a.name }, opp ? { id: opp.id, name: opp.name } : undefined);
    setExpandedId(null);
    setNotice(null);
    // One click: picking an account moves on to the next step. An account with a saved sizing moves on
    // once the restore has landed, so the next page opens on the restored numbers.
    const advance = () => {
      if (useCalculatorStore.getState().currentStepId === ACCOUNT_STEP) calculatorActions.next();
    };
    if (!hasSavedSizing(a)) {
      advance();
      return;
    }
    const linked = useCalculatorStore.getState().account;
    if (linked?.kind !== 'account') return;
    setNotice({ kind: 'restoring', name: a.name });
    fetchSavedSizing(apiFetch, linked, useCalculatorStore.getState().currentStepId)
      .then((res) => {
        if (token !== restoreToken.current) return;
        if (res.kind === 'restored') {
          applyLoaded(res.loaded);
          const rechecks = res.loaded.recheck.length;
          // This page is about to unmount, so the confirmation goes to the shell-level notice.
          showNotice({
            tone: 'info',
            text:
              `Restored the saved sizing for ${a.name}${res.savedAt ? ` from ${formatSavedDate(res.savedAt)}` : ''}.` +
              (rechecks > 0
                ? ` ${rechecks} ${rechecks === 1 ? 'answer needs' : 'answers need'} a re-check; they are flagged in the review.`
                : ''),
          });
          setNotice(null);
          advance();
        } else {
          setNotice(res.kind === 'unreadable' ? { kind: 'unreadable', name: a.name } : null);
          if (res.kind === 'none') advance();
        }
      })
      .catch((err: unknown) => {
        if (token !== restoreToken.current) return;
        setNotice({ kind: 'error', name: a.name, message: err instanceof Error ? err.message : 'The request failed.' });
      });
  }

  function clickAccount(a: OwnedAccount) {
    const mode = opportunityModeFor(a);
    if (mode.kind === 'link') link(a);
    else if (mode.kind === 'auto') link(a, mode.opportunity);
    else setExpandedId((prev) => (prev === a.id ? null : a.id));
  }

  // Demo mode stays pinned with the title and search; only the account rows scroll.
  const demoEntry = (
    <div key="demo" className="vc3-account vc3-account-demo" data-selected={demoSelected}>
      <button
        type="button"
        className="vc3-account-btn"
        aria-pressed={demoSelected}
        onClick={() => {
          calculatorActions.chooseDemo();
          calculatorActions.next();
        }}
      >
        <div>
          <div className="vc3-account-name">Demo mode</div>
          <div className="vc3-account-sub">Walk through with example numbers. Nothing is saved to Salesforce.</div>
        </div>
        {demoSelected && <Ph name="check-circle" size={20} />}
      </button>
    </div>
  );

  return (
    <section className="vc3-acct" aria-labelledby="vc3-account-h">
      <div className="vc3-eyebrow">Step 1</div>
      <h1 id="vc3-account-h" className="vc3-h1">
        Who are we sizing this for?
      </h1>
      <p className="vc3-lede">
        Pick one of your accounts to save the business case against, or use demo mode to walk through it
        without saving anything.
      </p>

      <input
        type="search"
        className="vc3-input"
        {...AUTOFILL_IGNORE_PROPS}
        style={{ maxWidth: 720 }}
        placeholder="Search your accounts…"
        aria-label="Search your accounts"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="vc3-acct-fixed">
        {notice && (
          <div
            className={`vc3-banner${notice.kind === 'error' || notice.kind === 'unreadable' ? ' vc3-banner-error' : ''}`}
            role={notice.kind === 'restoring' ? 'status' : 'alert'}
          >
            <span style={{ flex: 1 }}>
              {notice.kind === 'restoring' && <>Restoring the saved sizing for {notice.name}…</>}
              {notice.kind === 'unreadable' && (
                <>The saved sizing for {notice.name} could not be read, so the current sizing is unchanged.</>
              )}
              {notice.kind === 'error' && (
                <>The saved sizing for {notice.name} could not be loaded ({notice.message}). Pick the account again to retry.</>
              )}
            </span>
          </div>
        )}
        {load.status === 'error' && (
          <div className="vc3-banner vc3-banner-error" role="alert">
            <span style={{ flex: 1 }}>{load.message} Demo mode still works.</span>
            <button type="button" className="vc3-linkbtn" onClick={retry}>
              Retry
            </button>
          </div>
        )}
        {demoEntry}
      </div>

      <div className="vc3-accounts">
        {entries.map((entry) => {
          if (entry.kind === 'demo') return null; // pinned above the scrolling list

          const a = entry.account;
          const mode = opportunityModeFor(a);
          const expanded = mode.kind === 'expand' && expandedId === a.id;
          const selected = selectedAccountId === a.id;
          return (
            <div key={a.id} className="vc3-account" data-selected={selected}>
              <button
                type="button"
                className="vc3-account-btn"
                aria-expanded={mode.kind === 'expand' ? expanded : undefined}
                aria-pressed={mode.kind === 'expand' ? undefined : selected}
                onClick={() => clickAccount(a)}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="vc3-account-name">{a.name}</div>
                  {mode.kind === 'auto' && <div className="vc3-account-sub">{mode.opportunity.name}</div>}
                  {selected && account?.kind === 'account' && account.opportunityName && mode.kind === 'expand' && (
                    <div className="vc3-account-sub">{account.opportunityName}</div>
                  )}
                  {hasSavedSizing(a) && a.latestSnapshot && (
                    <div className="vc3-account-sub">
                      {a.latestSnapshot.createdDate && <>Saved {formatSavedDate(a.latestSnapshot.createdDate)}</>}
                      {a.latestSnapshot.totalAnnualValue != null && (
                        <> · {formatAnnualValue(a.latestSnapshot.totalAnnualValue)}</>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {hasSavedSizing(a) && <span className="vc3-saved-badge">Saved sizing</span>}
                  {mode.kind === 'expand' && <Ph name={expanded ? 'caret-up' : 'caret-down'} size={14} />}
                  {selected && <Ph name="check-circle" size={20} />}
                </div>
              </button>
              {expanded && (
                <div className="vc3-opps">
                  {a.openOpportunities.map((opp) => (
                    <button key={opp.id} type="button" className="vc3-opp" onClick={() => link(a, opp)}>
                      <span className="vc3-account-name">{opp.name}</span>
                      <span className="vc3-account-sub">
                        {[opp.stage, opp.closeDate ? `Close ${formatSavedDate(opp.closeDate)}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {load.status === 'loading' && (
          <>
            <div className="vc3-skeleton" />
            <div className="vc3-skeleton" />
            <div className="vc3-skeleton" />
          </>
        )}
        {load.status === 'ready' && accountList.length === 0 && (
          <div className="vc3-account-sub">No accounts are assigned to you yet. Demo mode is available above.</div>
        )}
        {load.status === 'ready' && accountList.length > 0 && entries.length === 1 && (
          <div className="vc3-account-sub">No accounts match &ldquo;{query}&rdquo;.</div>
        )}
      </div>
    </section>
  );
}
